import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Navbar from '../dashboard-landing/Navbar';
import Footer from '../dashboard-landing/Footer';
import { LockClosedIcon, EyeIcon, EyeSlashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from './ui/LoadingSpinner';

/**
 * ResetPassword Component
 * Allows users to set a new password using a token from their email
 */
const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: '',
    });

    const [email, setEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validatingToken, setValidatingToken] = useState(true);
    const [message, setMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState('');

    // Validate token and fetch email
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setMessage('Invalid or missing reset token. Please request a new password reset.');
                setValidatingToken(false);
                return;
            }

            try {
                const res = await api.get(`/api/forgot-password/validate-token`, {
                    params: { token }
                });

                if (res.data.success) {
                    setEmail(res.data.email);
                } else {
                    setMessage(res.data.message || 'Invalid reset token.');
                }
            } catch (err) {
                console.error('Token validation error:', err);
                setMessage(
                    err.response?.data?.message || 
                    'Invalid or expired reset token. Please request a new password reset.'
                );
            } finally {
                setValidatingToken(false);
            }
        };

        validateToken();
    }, [token]);

    // Password strength checker
    const checkPasswordStrength = (password) => {
        if (!password) {
            setPasswordStrength('');
            return;
        }

        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const isLongEnough = password.length >= 8;

        const strengthCount = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar, isLongEnough].filter(Boolean).length;

        if (strengthCount <= 2) {
            setPasswordStrength('weak');
        } else if (strengthCount === 3 || strengthCount === 4) {
            setPasswordStrength('medium');
        } else {
            setPasswordStrength('strong');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (name === 'newPassword') {
            checkPasswordStrength(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setSuccess(false);

        // Validation
        if (!formData.newPassword || !formData.confirmPassword) {
            setMessage('Please fill in all fields.');
            return;
        }

        if (formData.newPassword.length < 8) {
            setMessage('Password must be at least 8 characters long.');
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setMessage('Passwords do not match.');
            return;
        }

        setLoading(true);

        try {
            const res = await api.post(`/api/forgot-password/reset-password`, {
                token: token,
                newPassword: formData.newPassword,
            });

            if (res.data.success) {
                setSuccess(true);
                setMessage('Password reset successfully! Redirecting to login...');
                
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setMessage(res.data.message || 'Failed to reset password. Please try again.');
            }
        } catch (err) {
            console.error('Reset password error:', err);
            setMessage(
                err.response?.data?.message || 
                'Failed to reset password. The token may have expired. Please request a new password reset.'
            );
        } finally {
            setLoading(false);
        }
    };

    const getStrengthColor = () => {
        switch (passwordStrength) {
            case 'weak':
                return 'bg-red-500';
            case 'medium':
                return 'bg-yellow-500';
            case 'strong':
                return 'bg-green-500';
            default:
                return 'bg-gray-300';
        }
    };

    const getStrengthText = () => {
        switch (passwordStrength) {
            case 'weak':
                return 'Weak';
            case 'medium':
                return 'Medium';
            case 'strong':
                return 'Strong';
            default:
                return '';
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />

            {/* Loading Overlay */}
            {(loading || validatingToken) && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-8 shadow-2xl">
                        <LoadingSpinner message={validatingToken ? 'Validating token...' : 'Resetting password...'} />
                    </div>
                </div>
            )}

            <main className="flex-grow flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 space-y-6">
                    <div className="text-center">
                        {success ? (
                            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-600" />
                        ) : (
                            <LockClosedIcon className="mx-auto h-12 w-12 text-blue-600" />
                        )}
                        <h2 className="mt-4 text-2xl font-bold text-gray-900">
                            {success ? 'Success!' : 'Set New Password'}
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            {success 
                                ? 'Your password has been reset successfully.' 
                                : 'Please enter your new password below.'
                            }
                        </p>
                    </div>

                    {/* Message Display */}
                    {message && (
                        <div 
                            className={`p-4 rounded-md ${
                                success 
                                    ? 'bg-green-100 border-l-4 border-green-500 text-green-700' 
                                    : 'bg-red-100 border-l-4 border-red-500 text-red-700'
                            }`} 
                            role="alert"
                        >
                            <p>{message}</p>
                        </div>
                    )}

                    {/* Show form only if not successful and token exists */}
                    {!success && token && email && !validatingToken && (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Email Display (Read-only) */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    Resetting password for:
                                </label>
                                <div className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-medium">
                                    {email}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    This email address cannot be changed
                                </p>
                            </div>

                            {/* New Password */}
                            <div>
                                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                    New Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="newPassword"
                                        name="newPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        required
                                        value={formData.newPassword}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                        placeholder="Enter new password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? (
                                            <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                                <div className="mt-2 flex items-center">
                                    <input
                                        type="checkbox"
                                        id="newPassword-show"
                                        checked={showPassword}
                                        onChange={() => setShowPassword(!showPassword)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="newPassword-show" className="ml-2 text-sm text-gray-600">
                                        Show password
                                    </label>
                                </div>
                                
                                {/* Password Strength Indicator */}
                                {formData.newPassword && (
                                    <div className="mt-2">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs text-gray-600">Password Strength:</span>
                                            <span className={`text-xs font-medium ${
                                                passwordStrength === 'weak' ? 'text-red-600' :
                                                passwordStrength === 'medium' ? 'text-yellow-600' :
                                                'text-green-600'
                                            }`}>
                                                {getStrengthText()}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className={`h-2 rounded-full transition-all ${getStrengthColor()}`}
                                                style={{ 
                                                    width: passwordStrength === 'weak' ? '33%' : 
                                                           passwordStrength === 'medium' ? '66%' : 
                                                           passwordStrength === 'strong' ? '100%' : '0%' 
                                                }}
                                            ></div>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Use 8+ characters with uppercase, lowercase, numbers, and symbols
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        required
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                        placeholder="Confirm new password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showConfirmPassword ? (
                                            <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                                <div className="mt-2 flex items-center">
                                    <input
                                        type="checkbox"
                                        id="confirmPassword-show"
                                        checked={showConfirmPassword}
                                        onChange={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="confirmPassword-show" className="ml-2 text-sm text-gray-600">
                                        Show password
                                    </label>
                                </div>
                                {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                                    <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                                )}
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading || !token}
                                className={`w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-white font-medium transition-all ${
                                    loading || !token
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                                }`}
                            >
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>
                    )}

                    {/* Back to Login Link */}
                    <div className="text-center">
                        <Link
                            to="/login"
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium transition"
                        >
                            Back to Login
                        </Link>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default ResetPassword;
